import React, { useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, Download, X, CheckCircle, Truck, ShieldCheck } from "lucide-react";
import { EWayBill } from "@/lib/accounting/types";
import { generateQrSvgDataUri } from "@/lib/gst/qrCodeGenerator";

interface EWayBillSlipProps {
  bill: EWayBill | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Generate simple SVG barcode for 12-digit eway bill number
function generateBarcodeSvg(code: string): string {
  const bars = code.split("").map((digit, i) => {
    const val = parseInt(digit) || 1;
    const w = (val % 3) + 1;
    return `<rect x="${i * 14 + 10}" y="5" width="${w * 2}" height="45" fill="#000" />`;
  }).join("");
  
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 65" width="200" height="65">
    <rect width="100%" height="100%" fill="#fff"/>
    ${bars}
    <text x="100" y="60" font-family="monospace" font-size="11" font-weight="bold" text-anchor="middle">${code}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export const EWayBillSlip: React.FC<EWayBillSlipProps> = ({ bill, open, onOpenChange }) => {
  const printRef = useRef<HTMLDivElement>(null);

  if (!bill) return null;

  const handlePrint = () => {
    window.print();
  };

  const barcodeUri = generateBarcodeSvg(bill.ewayBillNumber.replace(/\s+/g, ''));
  const qrUri = generateQrSvgDataUri(`EWB:${bill.ewayBillNumber}|${bill.fromGstin}|${bill.toGstin}|${bill.invoiceValue}`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-6 bg-background">
        <DialogHeader className="flex flex-row items-center justify-between border-b pb-3">
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Truck className="h-5 w-5 text-primary" /> Official Form GST EWB-01 (E-Way Bill)
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handlePrint} className="gap-1.5">
              <Printer className="h-4 w-4" /> Print E-Way Bill
            </Button>
          </div>
        </DialogHeader>

        {/* Printable Area */}
        <div ref={printRef} className="print-content bg-white text-black p-6 rounded-md border border-neutral-300 font-sans text-xs">
          {/* Header */}
          <div className="text-center border-b-2 border-black pb-3 mb-4">
            <h2 className="text-base font-bold uppercase tracking-wide">Government of India</h2>
            <h3 className="text-sm font-bold uppercase">Form GST EWB-01</h3>
            <p className="text-[11px] text-neutral-600">(See Rule 138 of the Central Goods and Services Tax Rules, 2017)</p>
            <p className="text-[11px] font-semibold mt-1">E-Way Bill for Movement of Goods</p>
          </div>

          {/* Top Barcode and QR */}
          <div className="flex items-center justify-between border border-black p-3 mb-4 bg-neutral-50">
            <div>
              <p className="text-[11px] text-neutral-600 font-bold uppercase">E-Way Bill Number</p>
              <p className="text-lg font-mono font-bold tracking-wider">{bill.ewayBillNumber}</p>
              <img src={barcodeUri} alt="Barcode" className="h-12 mt-1" />
            </div>

            <div className="text-right flex items-center gap-4">
              <div>
                <p className="text-[11px]"><span className="font-bold">E-Way Bill Date:</span> {new Date(bill.validFrom).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                <p className="text-[11px] text-green-700 font-bold"><span className="text-neutral-700 font-bold">Valid From:</span> {new Date(bill.validFrom).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                <p className="text-[11px] text-red-700 font-bold"><span className="text-neutral-700 font-bold">Valid Until:</span> {new Date(bill.validUntil).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                <p className="text-[11px]"><span className="font-bold">Approx Distance:</span> {bill.distanceKm} KM</p>
              </div>
              <img src={qrUri} alt="QR Code" className="w-20 h-20 border border-neutral-300 p-1 bg-white" />
            </div>
          </div>

          {/* PART - A */}
          <div className="border border-black mb-4">
            <div className="bg-neutral-200 px-3 py-1.5 font-bold border-b border-black text-xs uppercase flex justify-between">
              <span>PART - A (Consignment & Tax Details)</span>
              <span>Status: <span className="text-green-800 uppercase font-black">{bill.status}</span></span>
            </div>
            <div className="grid grid-cols-2 divide-x divide-black text-xs">
              {/* Consignor */}
              <div className="p-3 space-y-1">
                <p className="font-bold text-neutral-700 uppercase text-[10px]">1. Consignor (Supplier / Dispatched From)</p>
                <p className="font-bold text-sm">{bill.fromName}</p>
                <p><span className="font-semibold">GSTIN:</span> <span className="font-mono font-bold">{bill.fromGstin}</span></p>
                <p><span className="font-semibold">Address:</span> {bill.fromAddress}</p>
                <p><span className="font-semibold">State & PIN:</span> {bill.fromState} - {bill.fromPincode}</p>
              </div>

              {/* Consignee */}
              <div className="p-3 space-y-1">
                <p className="font-bold text-neutral-700 uppercase text-[10px]">2. Consignee (Recipient / Shipped To)</p>
                <p className="font-bold text-sm">{bill.toName}</p>
                <p><span className="font-semibold">GSTIN:</span> <span className="font-mono font-bold">{bill.toGstin || "URP (Unregistered)"}</span></p>
                <p><span className="font-semibold">Address:</span> {bill.toAddress}</p>
                <p><span className="font-semibold">State & PIN:</span> {bill.toState} - {bill.toPincode}</p>
              </div>
            </div>

            {/* Document and Tax Table */}
            <div className="border-t border-black p-3 grid grid-cols-4 gap-2 text-xs bg-neutral-50/50">
              <div>
                <span className="font-bold text-neutral-600 block text-[10px]">Doc Type & Number</span>
                <span className="font-bold">{bill.documentType} - {bill.invoiceNumber || bill.voucherNumber}</span>
              </div>
              <div>
                <span className="font-bold text-neutral-600 block text-[10px]">Document Date</span>
                <span className="font-bold">{bill.invoiceDate}</span>
              </div>
              <div>
                <span className="font-bold text-neutral-600 block text-[10px]">Transaction Type</span>
                <span className="font-bold">{bill.supplyType} - {bill.subSupplyType}</span>
              </div>
              <div>
                <span className="font-bold text-neutral-600 block text-[10px]">Total Invoice Value</span>
                <span className="font-bold text-sm text-green-800">₹{bill.invoiceValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            {/* Goods Details */}
            <table className="w-full border-t border-black text-[11px]">
              <thead className="bg-neutral-100 border-b border-black font-bold">
                <tr>
                  <th className="p-2 text-left border-r border-black">HSN Code</th>
                  <th className="p-2 text-left border-r border-black">Product / Goods Description</th>
                  <th className="p-2 text-right border-r border-black">Quantity</th>
                  <th className="p-2 text-right border-r border-black">Taxable Val (₹)</th>
                  <th className="p-2 text-right border-r border-black">CGST (₹)</th>
                  <th className="p-2 text-right border-r border-black">SGST (₹)</th>
                  <th className="p-2 text-right border-r border-black">IGST (₹)</th>
                  <th className="p-2 text-right">Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 font-mono border-r border-black">{bill.hsnCode}</td>
                  <td className="p-2 font-medium border-r border-black">{bill.productName}</td>
                  <td className="p-2 text-right border-r border-black">{bill.quantity} {bill.unit}</td>
                  <td className="p-2 text-right border-r border-black font-semibold">₹{bill.taxableValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="p-2 text-right border-r border-black">₹{bill.cgstAmount.toFixed(2)}</td>
                  <td className="p-2 text-right border-r border-black">₹{bill.sgstAmount.toFixed(2)}</td>
                  <td className="p-2 text-right border-r border-black">₹{bill.igstAmount.toFixed(2)}</td>
                  <td className="p-2 text-right font-bold">₹{bill.invoiceValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* PART - B */}
          <div className="border border-black mb-4">
            <div className="bg-neutral-200 px-3 py-1.5 font-bold border-b border-black text-xs uppercase">
              PART - B (Vehicle & Transporter Details)
            </div>
            <table className="w-full text-xs">
              <thead className="bg-neutral-100 border-b border-black font-semibold text-[11px]">
                <tr>
                  <th className="p-2 text-left border-r border-black">Mode</th>
                  <th className="p-2 text-left border-r border-black">Vehicle / Doc Number</th>
                  <th className="p-2 text-left border-r border-black">Transporter ID & Name</th>
                  <th className="p-2 text-left border-r border-black">From Place</th>
                  <th className="p-2 text-left">Entered Date & Time</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 font-bold border-r border-black">{bill.transportMode}</td>
                  <td className="p-2 font-mono font-bold text-sm border-r border-black">{bill.vehicleNumber || bill.transportDocNumber || "—"}</td>
                  <td className="p-2 border-r border-black">{bill.transporterName || "Self / Express Logistics"}</td>
                  <td className="p-2 border-r border-black">{bill.fromState}</td>
                  <td className="p-2">{new Date(bill.validFrom).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Verification Footer */}
          <div className="flex justify-between items-center text-[10px] text-neutral-500 pt-2 border-t border-neutral-300">
            <div className="flex items-center gap-1.5 text-neutral-800">
              <ShieldCheck className="h-4 w-4 text-green-700" />
              <span>Certified valid under Goods and Services Tax Rules, 2017. Generated via AccouPro.</span>
            </div>
            <p>Page 1 of 1</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
